import { getExtensionFromFileName } from "../../common/utilities/file";
import {
  IMAGE_MIME_TYPES,
  MIME_TYPE_BY_EXTENSION,
  PDF_MIME_TYPE,
} from "../constants";

/**
 * What kind of file a document is: the mime type the pipeline stored, or what its
 * filename implies when there is none.
 *
 * The backend decides the type from the file's leading bytes and puts it on the
 * document, but it is null for anything created before that field existed and for
 * anything produced by a service still on an older library -- so the filename stays the
 * fallback, exactly as it was the only signal before. Mirrors `MimeTypes.resolve` in
 * api-crud and `resolved_mime_type` in the generic library; the three must agree.
 */
export const resolveMimeType = (document?: {
  name?: string;
  mimeType?: string | null;
}): string | null => {
  if (!document) {
    return null;
  }

  if (document.mimeType) {
    return document.mimeType;
  }

  const extension = document.name
    ? getExtensionFromFileName(document.name)?.toLowerCase()
    : undefined;

  return (extension && MIME_TYPE_BY_EXTENSION[extension]) || null;
};

/** Whether a document is an image the annotator renders as a single page. */
export const isImageDocument = (document?: {
  name?: string;
  mimeType?: string | null;
}): boolean => {
  const mimeType = resolveMimeType(document);
  return mimeType !== null && IMAGE_MIME_TYPES.includes(mimeType);
};

/**
 * Whether a document is a PDF.
 *
 * Falls back to the filename check this used to be, so a document without a stored mime
 * type behaves exactly as it did before the field existed.
 */
export const isPdfDocument = (document?: {
  name?: string;
  mimeType?: string | null;
}): boolean => {
  if (document?.mimeType) {
    return document.mimeType === PDF_MIME_TYPE;
  }

  return document?.name?.toLowerCase().includes(".pdf") ?? false;
};
